import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Calendar, Clock, User, Phone, Scissors, CreditCard, Home, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [bookingDetails, setBookingDetails] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('failed');
      setError('لم يتم العثور على معرف الجلسة');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await api.get(`/payments/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid' || response.data.status === 'complete') {
          setStatus('success');
          setBookingDetails(response.data);
        } else if (response.data.payment_status === 'expired' || response.data.status === 'expired') {
          setStatus('failed');
          setError(response.data.error || 'انتهت صلاحية جلسة الدفع. يرجى إعادة الحجز.');
        } else if (response.data.payment_status === 'failed' || response.data.status === 'failed') {
          setStatus('failed');
          setError(response.data.error || 'فشل الدفع');
        } else {
          // Still processing - check again (max 5 attempts)
          if (window.paymentCheckAttempts === undefined) window.paymentCheckAttempts = 0;
          window.paymentCheckAttempts++;
          
          if (window.paymentCheckAttempts < 5) {
            setTimeout(checkPaymentStatus, 2000);
          } else {
            setStatus('failed');
            setError('تعذر التحقق من حالة الدفع. يرجى التواصل مع الدعم.');
          }
        }
      } catch (err) {
        console.error('Error checking payment:', err);
        const errorDetail = err.response?.data?.detail || err.response?.data?.error;
        setStatus('failed');
        setError(errorDetail || 'فشل التحقق من حالة الدفع. يرجى التواصل مع الدعم.');
      }
    };

    checkPaymentStatus();
  }, [searchParams]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">جاري التحقق من الدفع...</h2>
          <p className="text-muted-foreground">يرجى الانتظار</p>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" dir="rtl">
        <div className="max-w-md w-full">
          <div className="bg-card border border-red-500/30 rounded-2xl p-8 text-center">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2">فشل الدفع</h1>
            <p className="text-red-400 mb-6">{error}</p>
            
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/public-booking')}
                className="w-full bg-primary text-black hover:bg-primary/90"
              >
                <ArrowRight className="w-4 h-4 ml-2" />
                حاول مرة أخرى
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full"
              >
                <Home className="w-4 h-4 ml-2" />
                العودة للرئيسية
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" dir="rtl">
      <div className="max-w-md w-full">
        <div className="bg-card border border-primary/30 rounded-2xl p-8">
          {/* Success Icon */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">تم الحجز بنجاح!</h1>
            <p className="text-muted-foreground">تم استلام الدفع وتأكيد حجزك</p>
          </div>

          {/* Booking Details */}
          <div className="bg-secondary/30 rounded-xl p-6 mb-6 space-y-4">
            <h2 className="text-lg font-semibold text-primary mb-4">تفاصيل الحجز</h2>
            
            {bookingDetails?.reference_number && (
              <div className="flex items-center justify-between py-2 border-b border-white/10">
                <span className="text-muted-foreground">رقم المرجع</span>
                <span className="text-white font-mono font-bold">{bookingDetails.reference_number}</span>
              </div>
            )}
            
            {bookingDetails?.booking_date && (
              <div className="flex items-center gap-3 py-2 border-b border-white/10">
                <Calendar className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">التاريخ</p>
                  <p className="text-white">{bookingDetails.booking_date}</p>
                </div>
              </div>
            )}
            
            {bookingDetails?.booking_time && (
              <div className="flex items-center gap-3 py-2 border-b border-white/10">
                <Clock className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">الوقت</p>
                  <p className="text-white">{bookingDetails.booking_time}</p>
                </div>
              </div>
            )}
            
            {bookingDetails?.service_name && (
              <div className="flex items-center gap-3 py-2 border-b border-white/10">
                <Scissors className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">الخدمة</p>
                  <p className="text-white">{bookingDetails.service_name}</p>
                </div>
              </div>
            )}
            
            {bookingDetails?.barber_name && (
              <div className="flex items-center gap-3 py-2 border-b border-white/10">
                <User className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">الحلاق</p>
                  <p className="text-white">{bookingDetails.barber_name}</p>
                </div>
              </div>
            )}
            
            {(bookingDetails?.amount_total || bookingDetails?.deposit_amount) && (
              <div className="flex items-center gap-3 py-2">
                <CreditCard className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-sm text-muted-foreground">العربون المدفوع</p>
                  <p className="text-green-500 font-bold">
                    {bookingDetails.amount_total || bookingDetails.deposit_amount} د.إ
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Important Note */}
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-6">
            <p className="text-sm text-primary">
              📱 تم إرسال تأكيد الحجز إلى رقم هاتفك. يرجى الحضور في الموعد المحدد.
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/')}
              className="w-full bg-primary text-black hover:bg-primary/90"
            >
              <Home className="w-4 h-4 ml-2" />
              العودة للرئيسية
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => navigate('/public-booking')}
              className="w-full"
            >
              حجز موعد آخر
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-muted-foreground text-sm">
            شكراً لاختيارك KE Gents Saloon
          </p>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccess;
